import { useCallback, useEffect, useRef, useState } from 'react';
import { requestJson } from '../utils/lifecycle';

/**
 * Loads one backend path and keeps it fresh.
 *
 * `loading` is true only while there is nothing to show yet. Background refreshes (polling, tab
 * focus, `refresh()`) keep the current data on screen and only report an error if there is none.
 */
export function useApi(path, { pollMs = 0, enabled = true } = {}) {
  const active = Boolean(path) && enabled;
  const [state, setState] = useState({ data: null, error: null, loading: active });
  const ticket = useRef(0);

  const fetchNow = useCallback(async (background) => {
    if (!active) return null;
    const mine = ++ticket.current;
    try {
      const data = await requestJson(path);
      if (mine === ticket.current) setState({ data, error: null, loading: false });
      return data;
    } catch (error) {
      if (mine === ticket.current) {
        setState((previous) => ({ data: previous.data, error: background && previous.data ? null : error, loading: false }));
      }
      return null;
    }
  }, [path, active]);

  useEffect(() => {
    if (!active) {
      setState({ data: null, error: null, loading: false });
      return undefined;
    }
    setState({ data: null, error: null, loading: true });
    fetchNow(false);

    const refreshQuietly = () => { if (document.visibilityState === 'visible') fetchNow(true); };
    const timer = pollMs > 0 ? setInterval(refreshQuietly, pollMs) : null;
    document.addEventListener('visibilitychange', refreshQuietly);
    return () => {
      // Bumping the ticket drops any response still in flight for this path.
      ticket.current += 1;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshQuietly);
    };
  }, [fetchNow, active, pollMs]);

  const retry = useCallback(() => {
    setState((previous) => ({ ...previous, error: null, loading: true }));
    return fetchNow(false);
  }, [fetchNow]);

  return { ...state, refresh: () => fetchNow(true), retry };
}
